import React, { useCallback, useEffect, useState } from 'react';
import { format, startOfMonth } from 'date-fns';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Button } from './ui/button';

const fmt = (v, unit) => `${Number(v || 0).toFixed(unit === 'liters' ? 1 : 0)}${unit === 'liters' ? ' L' : ''}`;

export const StockReconciliation = ({ API, L }) => {
  const [start, setStart] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [end, setEnd] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlyDiff, setOnlyDiff] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/cash-register/stock-reconciliation?start=${start}&end=${end}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error loading', 'Fehler beim Laden'));
    } finally { setLoading(false); }
  }, [API, start, end, L]);

  useEffect(() => { load(); }, [load]);

  const shown = onlyDiff ? rows.filter(r => Math.abs(r.difference) > 0.001) : rows;
  const diffCount = rows.filter(r => Math.abs(r.difference) > 0.001).length;

  return (
    <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm" data-testid="stock-reconciliation">
      <div className="p-5 border-b border-slate-100 flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h3 className="font-bold text-slate-900">{L('Stock reconciliation', 'Lager-Abgleich')}</h3>
          <p className="text-xs text-slate-500">{L('Opening + purchases − register sales = expected. Difference = actual − expected.', 'Anfangsbestand + Einkäufe − Kassenverkäufe = Sollbestand. Differenz = Ist − Soll.')}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Input type="date" value={start} onChange={e => setStart(e.target.value)} className="h-9 w-40 bg-slate-50" data-testid="recon-start" />
          <span className="text-slate-400 pb-2">–</span>
          <Input type="date" value={end} onChange={e => setEnd(e.target.value)} className="h-9 w-40 bg-slate-50" data-testid="recon-end" />
          <Button onClick={() => setOnlyDiff(v => !v)} variant="outline" className={`h-9 text-sm ${onlyDiff ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-slate-200 text-slate-600'}`} data-testid="recon-only-diff">
            {L('Only differences', 'Nur Differenzen')} ({diffCount})
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">{L('Item', 'Artikel')}</th>
              <th className="text-right px-3 py-2">{L('Opening', 'Anfang')}</th>
              <th className="text-right px-3 py-2">+ {L('Purchases', 'Einkäufe')}</th>
              <th className="text-right px-3 py-2">− {L('Sales', 'Kassenverkäufe')}</th>
              <th className="text-right px-3 py-2">= {L('Expected', 'Soll')}</th>
              <th className="text-right px-3 py-2">{L('Actual', 'Ist')}</th>
              <th className="text-right px-4 py-2">{L('Difference', 'Differenz')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-8 text-slate-400">…</td></tr>}
            {!loading && shown.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400" data-testid="recon-empty">{L('No differences', 'Keine Differenzen')}</td></tr>}
            {!loading && shown.map(r => {
              const bad = Math.abs(r.difference) > 0.001;
              return (
                <tr key={r.item_id} className={`border-t border-slate-100 ${bad ? 'bg-rose-50/60' : ''}`} data-testid={`recon-row-${r.item_id}`}>
                  <td className="px-4 py-2">
                    <p className="font-semibold text-slate-800">{r.name}</p>
                    <p className="text-[11px] text-slate-400">{r.category}{r.inventory_corrections ? ` · ${L('Count correction', 'Inventur-Korrektur')} ${r.inventory_corrections > 0 ? '+' : ''}${fmt(r.inventory_corrections, r.unit)}` : ''}</p>
                  </td>
                  <td className="text-right px-3 py-2 text-slate-600">{fmt(r.opening, r.unit)}</td>
                  <td className="text-right px-3 py-2 text-emerald-700">{fmt(r.purchases, r.unit)}</td>
                  <td className="text-right px-3 py-2 text-slate-600">{fmt(r.sold, r.unit)}</td>
                  <td className="text-right px-3 py-2 font-semibold text-slate-800">{fmt(r.expected, r.unit)}</td>
                  <td className="text-right px-3 py-2 font-semibold text-slate-800">{fmt(r.actual, r.unit)}</td>
                  <td className={`text-right px-4 py-2 font-bold ${bad ? 'text-rose-600' : 'text-emerald-600'}`} data-testid={`recon-diff-${r.item_id}`}>
                    {r.difference > 0 ? '+' : ''}{fmt(r.difference, r.unit)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
